import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { ordersApi } from 'modules/Orders/services/ordersApi'
import {
  CreateOrderProduct,
  CreateOrderState,
  OrderForm,
  Product,
} from 'modules/Orders/types'

import { initialOrderState } from './initialOrderState'

const calculateProductsAmount = (selectedProducts: CreateOrderProduct[]) => {
  return selectedProducts.reduce(
    (acc, cur) => +cur.price * cur.quantity + acc,
    0,
  )
}

const calculateProductsQuantity = (selectedProducts: CreateOrderProduct[]) => {
  return selectedProducts.reduce((acc, cur) => cur.quantity + acc, 0)
}

const initialState: CreateOrderState = {
  data: {},
  activeCompany: null,
  activeOrderForm: null,
  isValidateProducts: false,
}

export const CreateOrderSlice = createSlice({
  name: 'createOrder',
  initialState,
  reducers: {
    addSelectedProduct: (state, { payload }: PayloadAction<Product>) => {
      if (state.activeOrderForm && state.activeCompany) {
        const currentOrderForm =
          state.data?.[state.activeCompany]?.[state.activeOrderForm]
        if (!currentOrderForm) return
        const newProduct = currentOrderForm.selectedProducts.find(
          (product) => product.id === payload.id,
        )
        if (newProduct) {
          newProduct.quantity += 1
        } else {
          currentOrderForm.selectedProducts = [
            ...currentOrderForm.selectedProducts,
            { ...payload, quantity: 1 },
          ]
        }
        currentOrderForm.productsAmount = calculateProductsAmount(
          currentOrderForm.selectedProducts,
        )
        currentOrderForm.productsQuantity = calculateProductsQuantity(
          currentOrderForm.selectedProducts,
        )
      }
    },
    removeSelectedProduct: (state, { payload }: PayloadAction<string>) => {
      if (state.activeOrderForm && state.activeCompany) {
        const currentOrderForm =
          state.data?.[state.activeCompany]?.[state.activeOrderForm]
        if (!currentOrderForm) return
        currentOrderForm.selectedProducts =
          currentOrderForm.selectedProducts.filter(
            (product) => product.id !== payload,
          )
        currentOrderForm.productsAmount = calculateProductsAmount(
          currentOrderForm.selectedProducts,
        )
        currentOrderForm.productsQuantity = calculateProductsQuantity(
          currentOrderForm.selectedProducts,
        )
      }
    },
    plusProductQuantity: (state, { payload }: PayloadAction<string>) => {
      if (state.activeOrderForm && state.activeCompany) {
        const currentOrderForm =
          state.data?.[state.activeCompany]?.[state.activeOrderForm]
        if (!currentOrderForm) return
        const product = currentOrderForm.selectedProducts.find(
          (product) => product.id === payload,
        )
        if (product) {
          product.quantity += 1
          currentOrderForm.productsQuantity += 1
          currentOrderForm.productsAmount =
            currentOrderForm.productsAmount + +product.price
        }
      }
    },
    minusProductQuantity: (state, { payload }: PayloadAction<string>) => {
      if (state.activeOrderForm && state.activeCompany) {
        const currentOrderForm =
          state.data?.[state.activeCompany]?.[state.activeOrderForm]
        if (!currentOrderForm) return
        const product = currentOrderForm.selectedProducts.find(
          (product) => product.id === payload,
        )
        if (product) {
          product.quantity -= 1
          currentOrderForm.productsQuantity -= 1
          currentOrderForm.productsAmount =
            currentOrderForm.productsAmount - +product.price
        }
      }
    },
    clearState: (
      state,
      { payload }: PayloadAction<{ withBonuses?: boolean }>,
    ) => {
      if (state.activeOrderForm && state.activeCompany) {
        const currentOrderForm =
          state.data?.[state.activeCompany]?.[state.activeOrderForm]
        if (!currentOrderForm) return
        currentOrderForm.productsAmount = 0
        currentOrderForm.productsQuantity = 0
        currentOrderForm.selectedProducts = []
        if (payload.withBonuses) currentOrderForm.clientBonuses = 0
      }
    },
    changeUserBonus: (state, { payload }: PayloadAction<number>) => {
      if (state.activeOrderForm && state.activeCompany) {
        const currentOrderForm =
          state.data?.[state.activeCompany]?.[state.activeOrderForm]
        if (!currentOrderForm) return
        currentOrderForm.clientBonuses = payload
      }
    },
    changeActiveParams: (
      state,
      {
        payload,
      }: PayloadAction<{
        newOrderId?: string | null
        activeCompany?: string | null
      }>,
    ) => {
      if (payload.activeCompany || payload.activeCompany === null)
        state.activeCompany = payload.activeCompany
      if (state.activeCompany && !state.data[state.activeCompany]) {
        state.data[state.activeCompany] = {}
      }
      if (payload.newOrderId || payload.newOrderId === null)
        state.activeOrderForm = payload.newOrderId
      if (
        state.activeCompany &&
        state.activeOrderForm &&
        state.data[state.activeCompany] &&
        !state.data[state.activeCompany]![state.activeOrderForm]
      ) {
        state.data[state.activeCompany] ??= {}
        state.data[state.activeCompany]![state.activeOrderForm] =
          initialOrderState
      }
    },
    addOrderForm: (state, { payload }: PayloadAction<string>) => {
      if (state.activeCompany && state.data[state.activeCompany]) {
        state.data[state.activeCompany]![payload] = initialOrderState
      }
    },
    deleteOrderForm: (state, { payload }: PayloadAction<string>) => {
      if (state.activeCompany && state.data[state.activeCompany]) {
        delete state.data[state.activeCompany]![payload]
      }
    },
    updateOrderForm: (
      state,
      { payload }: PayloadAction<Partial<OrderForm>>,
    ) => {
      if (
        state.activeOrderForm &&
        state.activeCompany &&
        state.data[state.activeCompany]
      ) {
        Object.assign(
          state.data[state.activeCompany]![state.activeOrderForm],
          payload,
        )
      }
    },
    updateIsAvailableProduct: (
      state,
      { payload }: PayloadAction<{ productId: string; isAvailable: boolean }>,
    ) => {
      if (
        state.activeOrderForm &&
        state.activeCompany &&
        state.data[state.activeCompany]
      ) {
        const currentSelectedProduct = state.data[state.activeCompany]![
          state.activeOrderForm
        ].selectedProducts.find((product) => product.id === payload.productId)
        if (currentSelectedProduct)
          currentSelectedProduct.is_available = payload.isAvailable
      }
    },
    updateIsValidateProducts: (state, { payload }: PayloadAction<boolean>) => {
      state.isValidateProducts = payload
    },
    updateDeliverySlotIndex: (state, { payload }: PayloadAction<number>) => {
      if (state.activeCompany && state.activeOrderForm) {
        state.data[state.activeCompany]![
          state.activeOrderForm
        ].selectedDeliverySlotIndex = payload
      }
    },
  },
  extraReducers: (build) => {
    build.addMatcher(
      ordersApi.endpoints.searchClient.matchFulfilled,
      (state, { payload }) => {
        if (
          state.activeOrderForm &&
          state.activeCompany &&
          state.data[state.activeCompany]
        ) {
          const currentOrderForm =
            state.data[state.activeCompany]![state.activeOrderForm]
          if (payload.success && currentOrderForm)
            currentOrderForm.clientBonuses = payload.result.bonuses ?? 0
          else if (!payload.success && currentOrderForm)
            currentOrderForm.clientBonuses = 0
        }
      },
    )
  },
})

export const {
  addSelectedProduct,
  removeSelectedProduct,
  plusProductQuantity,
  minusProductQuantity,
  clearState,
  changeUserBonus,
  changeActiveParams,
  addOrderForm,
  deleteOrderForm,
  updateOrderForm,
  updateIsAvailableProduct,
  updateIsValidateProducts,
  updateDeliverySlotIndex,
} = CreateOrderSlice.actions
export const createOrderSlice = CreateOrderSlice.reducer
